import { downloadBufferAttachment } from 'features/shared/utility/attachmentHelper';
import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import FormSpinner from 'components/FormSpinner';
import { Box, Button, makeStyles, Fab, Tooltip } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import DownloadIcon from '@material-ui/icons/GetApp';
import Dialog from 'components/RMSDialog';
import { getAttachmentIcon } from 'features/shared/utility/attachmentIconHelper';
import RMSCheckbox2 from 'components/RMSCheckbox/RMSCheckbox2';
import RMSTextField from 'components/RMSTextField';
import RMSDictionary from 'components/RMSAutoComplete/RMSDictonary';
import RecordHistory from 'components/RecordHistory';
import RMSAutoComplete from 'components/RMSAutoComplete/RMSAutoComplete';
import RMSConfirmationModal from 'components/RMSModals/RMSConfirmationModal';
import { useError } from 'utils/formStyles';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { handleError } from 'reducers/ErrorReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { closeEditAttachment } from 'reducers/PersonDialogsReducer';
import {
  updateAttachment,
  getFileURLFromBuffer,
  getAttachmentById,
} from 'reducers/helpers/fileHelpers';

const useStyles = makeStyles((theme) => ({
  imageDialogLayout: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  imagePreviewPane: {
    flex: '0 0 40%',
    display: 'flex',
    // alignItems: 'flex-start',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1),
    maxHeight: 400,
    overflow: 'hidden',
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: 380,
    objectFit: 'contain',
  },
  detailsPane: {
    flex: '1 1 60%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0, // prevents flex child from overflowing on long text/fields
  },
  fileInfo: {
    minHeight: 35,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    '& img': {
      maxHeight: 24,
      borderRadius: 3,
      marginRight: 8,
    },
  },
  g: {
    minWidth: 350, // Only for non-preview
  },
  txtField: {
    width: '100%', // adjust to your existing non-image field width if different
  },
}));

function EditAttachment(props) {
  const classes = useStyles();
  const { FileType, dataURL, ptsAttachmentID } = props.data;

  const [FileName, setFileName] = useState('');
  const [attachmentBuffer, setAttachmentBuffer] = useState(dataURL ?? null);
  const [loading, setLoading] = useState(false);
  const [Description, setDescription] = useState('');
  const [IsDeleted, setIsDeleted] = useState(false);
  const [codeAttachmentType, setCodeAttachmentType] = useState();
  const [clearanceLevel, setClearanceLevel] = useState('');
  const [errors, setErrors] = useState();
  const [attachmentFormState, setAttachmentFormState] = useState({
    isValid: false,
    changes: false,
    values: {},
    touched: {},
    errors: {},
    formType: 'edit',
  });
  const [recordHistoryData, setRecordHistoryData] = useState({
    shouldVisible: true,
    isDisabled: true,
    createUpdateInfo: {},
  });
  const [showRecordHistory, setShowRecordHistory] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const secClearanceLevel = useSelector((state) => state.user.userClearanceOptions);
  const IMAGE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'tiff',
    'tif',
    'ico',
    'heic',
    'heif',
  ];
  const isImage = IMAGE_EXTENSIONS.includes(FileType);

  const {
    err,
    // isValidAndChanged,
    formChanged,
    isValid,
  } = useError(errors, setErrors);

  const ableToSave = isValid();

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleDiscardAndClose = () => {
    setOpenConfirmation(false);
    props.closeEditAttachment();
  };

  const handleSaveAndClose = () => {
    setOpenConfirmation(false);
    save();
  };

  useEffect(() => {
    setLoading(true);
    const {
      Description,
      IsDeleted,
      FileName,
      codeAttachmentType,
      Created,
      CreatedBy,
      Updated,
      UpdatedBy,
      ParentType,
    } = props.data;

    setRecordHistoryData({
      shouldVisible: true,
      isDisabled: !Created,
      createUpdateInfo: {
        created: Created,
        createdBy: CreatedBy,
        updated: Updated,
        updatedBy: UpdatedBy,
      },
    });

    if (ParentType === 'INCIDENT') setClearanceLevel(props.data?.ClearanceLevel);
    setDescription(Description);
    setIsDeleted(Boolean(IsDeleted));
    setFileName(FileName.substr(0, FileName.length - FileType.length - 1));
    setCodeAttachmentType(codeAttachmentType);
  }, []);

  useEffect(() => {
    if (!attachmentBuffer) {
      getAttachmentById(ptsAttachmentID)
        .then((result) => {
          setAttachmentBuffer(result?.[0]?.FileObject ?? null);
          setLoading(false);
        })
        .catch(() => {
          props.handleError();
          setLoading(false);
        });
    } else setLoading(false);
  }, [attachmentBuffer, ptsAttachmentID]);

  const close = () => {
    if (errors?.changed) {
      setOpenConfirmation(true);
      return;
    }
    props.closeEditAttachment();
  };

  const save = () => {
    const type = props.data.ParentType;
    const data = {
      ptsAttachmentID: props.data.ptsAttachmentID,
      FileName: `${FileName}.${FileType}`,
      Description,
      IsDeleted: IsDeleted ? 1 : 0,
      codeAttachmentType: codeAttachmentType,
    };
    if (type === 'INCIDENT') data.ClearanceLevel = clearanceLevel;

    props.showSpinner();
    updateAttachment(data)
      .then(() => {
        props.notifyDataUpdate({ type: 'file-upload', data: type });
        props.closeEditAttachment();
      })
      .catch(props.handleError)
      .finally(props.hideSpinner);
  };

  const handleCloseRecordHistoryDialog = () => setShowRecordHistory(false);

  const OnDownloadClick = () => downloadBufferAttachment(attachmentBuffer, FileType, FileName);

  const renderActions = () => {
    return (
      <>
        {recordHistoryData?.shouldVisible && (
          <Box className={classes.btn}>
            <RecordHistory
              showRecordHistory={showRecordHistory}
              setShowRecordHistory={setShowRecordHistory}
              disabled={recordHistoryData?.isDisabled}
              handleCloseRecordHistoryDialog={handleCloseRecordHistoryDialog}
              createUpdateInfo={recordHistoryData?.createUpdateInfo}></RecordHistory>
          </Box>
        )}

        {attachmentBuffer && (
          <Tooltip title="Download">
            <Fab size="small" color="secondary" aria-label="citation download">
              <DownloadIcon onClick={OnDownloadClick} style={{ margin: '0px' }} />
            </Fab>
          </Tooltip>
        )}

        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={save}
        // disabled={!isValidAndChanged()}
        >
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const renderFileName = () => {
    const onChange = (ev, val) => {
      setFileName(val);
      formChanged();
    };
    const error = err(FileName, 'FileName', 'File name cannot be empty');
    return (
      <div>
        <RMSTextField
          label="File Name"
          value={FileName}
          onChange={onChange}
          className={classes.txtField}
          error={error}
          max={255}
        />
      </div>
    );
  };

  const renderDescription = () => {
    const onChange = (ev, val) => {
      setDescription(val);
      formChanged();
    };
    return (
      <div>
        <RMSTextField
          label="Description"
          value={Description}
          onChange={onChange}
          className={classes.txtField}
          compact
          max={50}
        />
      </div>
    );
  };

  const renderIsDeleted = () => {
    const onChange = (ev, val) => {
      setIsDeleted(val);
      formChanged();
    };
    return (
      <div>
        <RMSCheckbox2 checked={IsDeleted} onChange={onChange} label="Is Deleted" />
      </div>
    );
  };

  const renderAttachmentType = () => {
    return (
      <RMSDictionary
        compact
        options="codeAttachmentTypes"
        value={{ Code: codeAttachmentType }}
        className={classes.item2}
        onChange={(e, value) => {
          setCodeAttachmentType(value ? value.Code : '');
          formChanged();
        }}
        label="Type"
      />
    );
  };

  const renderClearanceLevel = () => {
    return (
      <RMSAutoComplete
        size="small"
        id="combo-attachment-clearance-level"
        autoComplete
        autoSelect
        autoHighlight
        options={secClearanceLevel}
        label="Clearance Level"
        serviceName="secClearanceLevel"
        variant="outlined"
        title={'Level'}
        formType={'edit'} // ClearanceLevel dictionary table do not have default option column
        stateHolder={attachmentFormState}
        setStateHolder={setAttachmentFormState}
        stateHolderAttribute="clearanceLevel" //need to review
        setField={(e, value) => {
          setClearanceLevel(value);
          formChanged();
        }}
        value={clearanceLevel} //need to review
        className="mb-2"
      />
    );
  };

  return (
    <>
      {loading && <FormSpinner />}
      <Dialog toolbar onClose={close} title="Edit Attachment" actions={renderActions()}>
        <div className={isImage ? classes.imageDialogLayout : classes.g}>
          {isImage && attachmentBuffer && (
            <div className={classes.imagePreviewPane}>
              <img
                src={getFileURLFromBuffer(attachmentBuffer, FileType)}
                alt=""
                className={classes.imagePreview}
              />
            </div>
          )}
          <div className={isImage ? classes.detailsPane : ''}>
            <div className={classes.fileInfo}>
              <img src={getAttachmentIcon(FileType)} alt="" />
              {props.data.FileName}
            </div>
            {renderFileName()}
            {renderAttachmentType()}
            {props.data?.ParentType === 'INCIDENT' && renderClearanceLevel()}
            {renderDescription()}
            {renderIsDeleted()}
          </div>
        </div>
      </Dialog>
      <RMSConfirmationModal
        title="You have unsaved changes in this form!"
        text="Do you want to save changes?"
        saveBtnName="Yes"
        btnName="No"
        noBtnName="Cancel"
        open={openConfirmation}
        btnDisabled={!ableToSave}
        closeDialog={handleCloseConfirmation}
        handleSubmit={handleDiscardAndClose}
        handleModalSave={handleSaveAndClose}
      />
    </>
  );
}

export default connect(null, {
  closeEditAttachment,
  showSpinner,
  hideSpinner,
  handleError,
  notifyDataUpdate,
})(EditAttachment);
